/**
 * GameOverScreen Component
 *
 * Displayed when the player is defeated by a gym leader.
 * Shows final Pokemon stats, grade, aptitudes, and earned inspirations.
 */

import React, { useEffect, useState } from 'react';
import { ArrowLeft, Star, XCircle, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { useGame } from '../contexts/GameContext';
import { useInventory } from '../contexts/InventoryContext';
import { useTutorial } from '../contexts/TutorialContext';
import {
  generatePokemonSprite,
  getGradeColor,
  getPokemonGrade,
  getAptitudeColor,
  StatIcon
} from '../utils/gameUtils';
import { TypeBadge, TypeIcon } from '../components/TypeIcon';
import TutorialOverlay from '../components/TutorialOverlay';
import TutorialRewardModal from '../components/TutorialRewardModal';

// NOTE: Inspiration generation removed - now handled server-side
// Backend generates inspirations in routes/career/progression.js POST /complete
// Using B+ aptitude filtering with weighted star rarity (20% 1★, 70% 2★, 10% 3★)

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 15 },
  visible: { opacity: 1, y: 0 }
};

const GameOverScreen = () => {
  const { setGameState, setSelectedPokemon, setSelectedSupports, completedCareerData, setCompletedCareerData } = useGame();
  const { loadTrainedPokemon, loadPrimos } = useInventory();
  const { tutorialActive, advanceStep, isTutorialStep, completeTutorial, showRewardModal, setShowRewardModal } = useTutorial();

  const [showTutorialComplete, setShowTutorialComplete] = useState(false);

  const typeMap = { Fire: 'Fire', Water: 'Water', Grass: 'Grass', Electric: 'Electric', Psychic: 'Psychic', Fighting: 'Fighting', Dark: 'Dark', Dragon: 'Dragon', Steel: 'Steel' };

  // Tutorial: Advance to gacha-intro when FIRST career ends
  // Or show tutorial-complete when SECOND career ends (when on support-select-intro step)
  useEffect(() => {
    if (tutorialActive && completedCareerData) {
      if (isTutorialStep('support-select-intro')) {
        // Second career just ended - show tutorial-complete
        console.log('[GameOverScreen] Second career ended - showing tutorial-complete');
        advanceStep('tutorial-complete');
        setShowTutorialComplete(true);
      } else {
        console.log('[GameOverScreen] First career ended - advancing to gacha-intro');
        advanceStep('gacha-intro');
      }
    }
  }, [tutorialActive, completedCareerData, advanceStep, isTutorialStep]);

  // Use inspirations from backend (generated server-side in POST /complete)
  const inspirations = completedCareerData?.inspirations || null;

  const handleReturn = () => {
    setGameState('menu');
    setSelectedPokemon(null);
    setSelectedSupports([]);
    setCompletedCareerData(null);
    loadTrainedPokemon();
  };

  // If no career data, show loading or error state
  if (!completedCareerData) {
    return (
      <div className="min-h-screen bg-pocket-bg p-4">
        <motion.header
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="sticky top-0 z-10 bg-white shadow-card rounded-2xl mb-4 max-w-lg mx-auto"
        >
          <div className="flex items-center justify-between px-4 py-3">
            <button
              onClick={handleReturn}
              className="p-2 text-pocket-text-light hover:text-pocket-text hover:bg-pocket-bg rounded-lg transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <div className="flex items-center gap-2">
              <XCircle size={20} className="text-pocket-red" />
              <span className="font-bold text-pocket-text">Career Complete</span>
            </div>
            <div className="w-10" />
          </div>
        </motion.header>

        <div className="max-w-lg mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-card p-8 text-center"
          >
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
              <XCircle size={32} className="text-pocket-red" />
            </div>
            <h1 className="text-2xl font-bold text-pocket-text mb-2">Career Complete</h1>
            <p className="text-pocket-text-light mb-6">Loading career results...</p>
            <button onClick={handleReturn} className="pocket-btn-purple px-6 py-3">
              Return to Menu
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  const pokemonName = completedCareerData.pokemon?.name || 'Unknown';
  const pokemonType = completedCareerData.pokemon?.primaryType || 'Normal';
  const finalStats = completedCareerData.currentStats || {};
  const aptitudes = completedCareerData.pokemon?.typeAptitudes || {};
  const grade = getPokemonGrade(finalStats);
  const finalTurn = completedCareerData.turn || 0;
  const gymsDefeated = completedCareerData.currentGymIndex || 0;

  return (
    <div className="min-h-screen bg-pocket-bg p-4">
      {/* Header */}
      <motion.header
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="sticky top-0 z-10 bg-white shadow-card rounded-2xl mb-4 max-w-lg mx-auto"
      >
        <div className="flex items-center justify-between px-4 py-3">
          <button
            onClick={handleReturn}
            className="p-2 text-pocket-text-light hover:text-pocket-text hover:bg-pocket-bg rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex items-center gap-2">
            <XCircle size={20} className="text-pocket-red" />
            <span className="font-bold text-pocket-text">Career Complete</span>
          </div>
          <div className="w-10" />
        </div>
      </motion.header>

      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="max-w-lg mx-auto"
      >
        {/* Defeat Banner */}
        <motion.div
          variants={itemVariants}
          className="bg-gradient-to-r from-gray-600 to-gray-700 rounded-2xl shadow-card-lg p-6 mb-4 text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.3, type: 'spring', stiffness: 200 }}
            className="w-20 h-20 mx-auto mb-3 rounded-full bg-white/20 flex items-center justify-center"
          >
            <XCircle size={40} className="text-white" />
          </motion.div>
          <h1 className="text-3xl font-bold text-white mb-1">Career Complete</h1>
          <p className="text-white/90 text-sm">
            Defeated by gym leader
          </p>
          <p className="text-white/70 text-xs mt-1">
            Turn {finalTurn} | Gyms Defeated: {gymsDefeated}/5
          </p>
          {completedCareerData.primosReward > 0 && (
            <div className="mt-3 bg-white/20 rounded-lg px-4 py-2 inline-flex items-center gap-2">
              <Sparkles className="text-white" size={18} />
              <span className="text-white font-bold">+{completedCareerData.primosReward} Primos</span>
              <Sparkles className="text-white" size={18} />
            </div>
          )}
        </motion.div>

        {/* Pokemon Card */}
        <motion.div
          variants={itemVariants}
          className="bg-white rounded-2xl shadow-card p-5 mb-4"
        >
          <div className="flex justify-center mb-3">
            {generatePokemonSprite(pokemonType, pokemonName, 64, false)}
          </div>
          <h2 className="text-xl font-bold text-pocket-text text-center mb-2">{pokemonName}</h2>
          <div className="flex justify-center mb-3">
            <TypeBadge type={pokemonType} size={16} />
          </div>
          <div className="flex justify-center mb-4">
            <span
              className="px-4 py-1.5 rounded-full text-sm font-bold text-white"
              style={{ backgroundColor: getGradeColor(grade) }}
            >
              Grade: {grade}
            </span>
          </div>

          {/* Final Stats */}
          <div className="bg-pocket-bg rounded-xl p-4 mb-4">
            <h3 className="font-bold text-pocket-text text-sm mb-3 text-center">Final Stats</h3>
            <div className="grid grid-cols-5 gap-2 text-xs">
              {Object.entries(finalStats).map(([stat, value]) => (
                <div key={stat} className="flex flex-col items-center gap-1">
                  <StatIcon stat={stat} size={14} />
                  <span className="font-bold text-pocket-text">{value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Type Aptitudes */}
          {Object.keys(aptitudes).length > 0 && (
            <div className="bg-blue-50 rounded-xl p-4">
              <h3 className="font-bold text-blue-700 text-sm mb-3 text-center">Type Aptitudes</h3>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {Object.entries(aptitudes).map(([aptitude, aptGrade]) => {
                  const typeName = typeMap[aptitude] || aptitude;
                  return (
                    <div
                      key={aptitude}
                      className="flex justify-between items-center px-2 py-1 rounded bg-white"
                    >
                      <TypeBadge type={typeName} size={14} />
                      <span
                        className="px-2 py-0.5 rounded text-white font-bold"
                        style={{ backgroundColor: getAptitudeColor(aptGrade) }}
                      >
                        {aptGrade}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </motion.div>

        {/* Inspirations */}
        {inspirations && (
          <motion.div
            variants={itemVariants}
            className="bg-white rounded-2xl shadow-card p-5 mb-4"
          >
            <div className="flex items-center justify-center gap-2 mb-4">
              <Star size={18} className="text-type-psychic" />
              <h3 className="font-bold text-pocket-text">Inspirations Earned</h3>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {/* Stat Inspiration */}
              <div className="bg-purple-50 rounded-xl p-3 border-2 border-purple-200">
                <div className="text-[10px] font-bold text-type-psychic mb-1">STAT</div>
                <div className="font-bold text-pocket-text mb-1 text-sm">{inspirations.stat.name}</div>
                <div className="text-xs text-pocket-text-light mb-2">Value: {inspirations.stat.value}</div>
                <div className="flex gap-0.5">
                  {[...Array(inspirations.stat.stars)].map((_, i) => (
                    <Star key={i} size={14} className="text-amber-400 fill-amber-400" />
                  ))}
                </div>
              </div>

              {/* Aptitude Inspiration */}
              <div className="bg-purple-50 rounded-xl p-3 border-2 border-purple-200">
                <div className="text-[10px] font-bold text-type-psychic mb-1">APTITUDE</div>
                <div className="font-bold text-pocket-text mb-1 text-sm">{inspirations.aptitude.name}</div>
                <div className="text-xs text-pocket-text-light mb-2">Grade: {inspirations.aptitude.grade}</div>
                <div className="flex gap-0.5">
                  {[...Array(inspirations.aptitude.stars)].map((_, i) => (
                    <Star key={i} size={14} className="text-amber-400 fill-amber-400" />
                  ))}
                </div>
              </div>

              {/* Strategy Inspiration */}
              {inspirations.strategy && (
                <div className="bg-purple-50 rounded-xl p-3 border-2 border-purple-200">
                  <div className="text-[10px] font-bold text-type-psychic mb-1">STRATEGY</div>
                  <div className="font-bold text-pocket-text mb-1 text-sm">{inspirations.strategy.name}</div>
                  <div className="text-xs text-pocket-text-light mb-2">Grade: {inspirations.strategy.grade}</div>
                  <div className="flex gap-0.5">
                    {[...Array(inspirations.strategy.stars)].map((_, i) => (
                      <Star key={i} size={14} className="text-amber-400 fill-amber-400" />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Return Button */}
        <motion.div variants={itemVariants}>
          <button
            onClick={handleReturn}
            className="w-full pocket-btn-purple py-4 text-lg"
          >
            Return to Menu
          </button>
        </motion.div>
      </motion.div>

      {/* Tutorial overlay for tutorial-complete step (after second career) */}
      {showTutorialComplete && (
        <TutorialOverlay
          stepId="tutorial-complete"
          onContinue={() => {
            completeTutorial();
            setShowTutorialComplete(false);
            // Refresh primos to show the 5000 reward
            loadPrimos();
          }}
        />
      )}

      {/* Tutorial Reward Modal - shows 5000 primos reward */}
      <TutorialRewardModal
        isOpen={showRewardModal}
        onClose={() => {
          setShowRewardModal(false);
          handleReturn();
        }}
      />
    </div>
  );
};

export default GameOverScreen;
